import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Reservation } from '@app/shared/interfaces/lib/reservation.interface';
import { environment } from '@env/environment';
import { format } from 'date-fns';
import { catchError, map, Observable, of } from 'rxjs';

interface DirectAlterationMoneyValue {
  currency: string;
  amount: string;
  formatted_string: string;
  formatted_decimal: string;
}

interface DirectAlterationLineItem {
  title: string;
  value: DirectAlterationMoneyValue;
  metadata: {
    pricing_key: string;
    capabilities: {
      can_edit: boolean;
      can_remove: boolean;
    };
  };
}

interface DirectAlterationGuests {
  adults: number;
  children: number;
  infants: number;
  pets: number;
}

interface DirectAlterationReservationState {
  total: DirectAlterationMoneyValue;
  lines: DirectAlterationLineItem[];
  checkin: string;
  checkin_full: string;
  checkin_time: string;
  checkout: string;
  checkout_full: string;
  checkout_time: string;
  num_guests: DirectAlterationGuests;
}

interface DirectAlterationFinancialSplit {
  payout: DirectAlterationMoneyValue;
  serviceFee: DirectAlterationMoneyValue;
  retainedTaxes: DirectAlterationMoneyValue;
  securityDeposit: DirectAlterationMoneyValue;
  total: DirectAlterationMoneyValue;
}

interface DirectAlterationFinancialActions {
  refund_splits: DirectAlterationFinancialSplit;
  charge_splits: DirectAlterationFinancialSplit;
  changed_splits: DirectAlterationFinancialSplit;
  diff_lines: DirectAlterationLineItem[];
  has_payment_terms: boolean;
  should_recalculate_payment_terms: boolean;
  should_clear_payment_terms: boolean;
  payment_terms: unknown | null;
}

export interface DirectAlteration {
  property: {
    id: number;
    person_capacity: number;
  };
  reservation_code: string;
  alteration_uuid: string;
  initiated_by: string;
  requested_at: string;
  accepted_at: string | null;
  rejected_at: string | null;
  voided_at: string | null;
  expires_at: string | null;
  current_state: DirectAlterationReservationState;
  previous_state: DirectAlterationReservationState;
  financial_actions: DirectAlterationFinancialActions;
  reason: string | null;
  difference: string;
}

export type DirectAlterationInitiatedBy = 'host' | 'guest';
export type DirectAlterationExpiration = '24 hours' | '48 hours' | '72 hours';

export interface AlterationRequestPayload {
  start_date: string;
  end_date: string;
  number_of_adults: number;
  number_of_children: number;
  number_of_infants: number;
  number_of_pets: number;
  initiated_by: DirectAlterationInitiatedBy;
  refund_amount?: number | null;
  expires_in?: DirectAlterationExpiration | null;
  pricing?: {
    nightly_total?: number;
    fees?: {
      [key: string]: number;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class DirectAlterationsService {
  http = inject(HttpClient);

  fetchAlteration(reservationId: string): Observable<DirectAlteration> {
    return this.http
      .get<{ data: DirectAlteration }>(`${environment.apiUrl}/alterations/direct/${reservationId}`)
      .pipe(map((res) => res.data));
  }

  fetchPriceQuote(
    reservationId: string,
    alteration: AlterationRequestPayload
  ): Observable<{ quote: DirectAlteration | null; error: string | null }> {
    return this.http
      .post<{ data: DirectAlteration }>(`${environment.apiUrl}/alterations/direct/${reservationId}/quote`, alteration)
      .pipe(
        map((res) => ({ quote: res.data, error: null })),
        catchError((error) =>
          of({ quote: null, error: error.error?.message || 'An error occurred generating a quote' })
        )
      );
  }

  submitAlteration(reservationId: string, alteration: AlterationRequestPayload): Observable<DirectAlteration> {
    return this.http
      .post<{ data: DirectAlteration }>(`${environment.apiUrl}/alterations/direct/${reservationId}`, alteration)
      .pipe(map((res) => res.data));
  }

  generateAlterationPayloadFromReservation(
    reservation: Reservation,
    initiatedBy: DirectAlterationInitiatedBy = 'host'
  ): AlterationRequestPayload {
    return {
      start_date: format(reservation.checkin, 'YYYY-MM-DD'),
      end_date: format(reservation.checkout, 'YYYY-MM-DD'),
      number_of_adults: reservation.num_adults,
      number_of_children: reservation.num_children,
      number_of_infants: reservation.num_infants,
      number_of_pets: reservation.num_pets,
      initiated_by: initiatedBy,
    };
  }

  generateQuotePayloadFromAlteration(alteration: DirectAlteration): AlterationRequestPayload {
    return {
      start_date: alteration.current_state.checkin,
      end_date: alteration.current_state.checkout,
      number_of_adults: alteration.current_state.num_guests.adults,
      number_of_children: alteration.current_state.num_guests.children,
      number_of_infants: alteration.current_state.num_guests.infants,
      number_of_pets: alteration.current_state.num_guests.pets,
      initiated_by: alteration.initiated_by as DirectAlterationInitiatedBy,
    };
  }
}
